import clsx from 'clsx'
import React, { useState, useEffect, useRef } from 'react'
import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import Tooltip from '@mui/material/Tooltip'
import Popover from '@mui/material/Popover'

import ErrorCodes from '@/enums/error-codes.enum'
import { userLoggedOut } from '@/redux/ducks/global'
import { setTenantName as setTenantNameAction, switchTenant, fetchData } from '@/redux/ducks/user/info'

import { Formik, Form } from 'formik'

import accountConfig from '@/modules/account/config'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ContextMenuItem from '@/modules/base/components/context-menu/context-menu-item/context-menu-item'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { TextInput } from '@/modules/base/components/inputs'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import UserHeader from './user-header'
import TenantInitialsIcon from '@/modules/my-account/tenant-information/tenant-initials-icon'

import LogoColorIcon from '@assets/images/logo/svg/logo-color.svg'
import IconUser from '@assets/images/icons/userPlaceholder.svg'
import ArrRightSvg from 'assets/images/icons/arr_right.svg'
import IconPencil from '@assets/images/icons/pencil.svg'
import IconLogout from '@assets/images/icons/logout.svg'
import IconCheck from '@assets/images/icons/check.svg'
import IconCancel from '@assets/images/icons/close.svg'
import WarningIcon from '@assets/images/icons/exclamation_mark2.svg'

import lang from '@/lang'
import theme from '@/theme'
import { isMobileScreen } from '@/modules/_helpers/detectMobile'
import MenuOverlay from '@/modules/layout/components/menu/overlay/index'
import useInvites from '@/modules/client/_helpers/useInvites'
import TenantInitialsBubble from '@/modules/my-account/tenant-information/tenant-initials-bubble'
import AccountPopover from '@/modules/my-account/account-popover/account-popover'
import { ConfirmModal } from '@/modules/base/components/modal'
import Loader from '@/modules/base/components/loader/loader'
import { useNavigate } from 'react-router'

const ITEM_ICON_DIMENSIONS = { height: '16px', width: '16px' }

const contextMenuItemNoHoverStyle = {
  '&&:hover': {
    backgroundColor: theme.white,
    border: '1px solid transparent',
    borderTop: theme.border,
    borderLeft: 'unset',
    borderRight: 'unset',
    fontWeight: theme.fontWeights.medium,
  },
}

const useStyles = makeStyles(theme => ({
  menuWrapper: ({ tenantCreating, demoTenantCreating }) => ({
    padding: tenantCreating || demoTenantCreating ? '0' : '30px 30px 2px 30px',
    width: 500,
    [theme.devices.mobile]: {
      width: '100%',
    },
  }),
  avatarIcon: {
    width: '100%',
    height: '100%',
    '& .st0': {
      fill: theme.white,
    },
  },
  avatarButton: (props) => ({
    marginBottom: 10,
    marginTop: 14,
    marginLeft: 3,
    borderRadius: '50%',
    border: props.hasConfirmedEmail && !props.hasDanglingSubscriptions ? `2px solid ${theme.primaryDark}` : `2px solid ${theme.red}`,
    height: 48,
    width: 48,
    display: 'flex',
    '& img': {
      borderRadius: '50%',
      width: '100%',
    },
    '& :nth-child(2)': {
      position: 'absolute !important',
      top: 48,
      left: 52,
      fontSize: theme.fontSizes.smaller,
    },
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.navMenuItemBackgroundColor,
      borderRadius: '50%',
    },
  }),
  avatar: {
    width: 24,
    height: 24,
    backgroundColor: theme.iconColor,
    borderRadius: '50%',
    justifyContent: 'center',
    color: theme.white,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    '&.menu': {
      width: 40,
      height: 40,
      fontSize: theme.fontSizes.smedium,
      fontWeight: theme.fontWeights.regular,
    },
  },
  avatarDemo: {
    width: 24,
    height: 24,
    borderColor: theme.iconColor,
    backgroundColor: theme.iconColor,
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: '50%',
    justifyContent: 'center',
    color: theme.white,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    '&.menu': {
      width: 40,
      height: 40,
    },
    '& > *': {
      height: 20,
      width: 20,
    },
  },

  contextMenuItem: {
    borderTop: theme.border,
    padding: '14px 0',
    borderLeft: 'none',
    ...contextMenuItemNoHoverStyle,
  },
  contextMenuItemLogout: {
    borderTop: theme.border,
    alignItems: 'center',
    padding: '20px 0',
    borderLeft: 'none',
    ...contextMenuItemNoHoverStyle,
  },
  availableTenant: {
    color: theme.textColor,
    backgroundColor: theme.white,
    border: theme.border,
  },
  currentTenant: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '16px 30px',
    margin: '30px -30px -1px -30px',
    width: 500,
    border: theme.border,
    backgroundColor: theme.backgroundColor,
  },
  [theme.devices.mobile]: {
    avatar: {
      height: '100%',
      width: '100%',
    },
    avatarDemo: {
      height: '100%',
      width: '100%',
    },
    avatarPosition: {
      position: 'absolute',
      margin: 0,
      height: 24,
      width: 24,
      right: 20,
    },
    currentTenant: {
      width: '100vw',
    },
  },
  separator: {
    width: '100%',
    paddingLeft: 17,
    paddingRight: 17,
    paddingTop: 10,
    marginTop: 17,
    textTransform: 'uppercase',
    '&.big': {
      marginTop: 40,
    },
  },
  currentTenantDetail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: 3,
    flexGrow: 1,
  },
  inputContainer: {
    marginLeft: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  inputForm: {
    display: 'flex',
    height: 25,
    marginTop: 0,
    marginBottom: 0,
  },
  inputField: {
    '& > div > input': {
      height: '25px !important',
    },
  },
  currentTenantName: {
    marginLeft: 14,
    marginRight: 17,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  displayName: {
    color: theme.textColor,
    fontWeight: theme.fontWeights.semiBold,
    fontSize: theme.fontSizes.smedium,
    maxWidth: 300,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  iconPencil: {
    cursor: 'pointer',
    fill: theme.textColor,
  },
  iconStyle: {
    height: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuItemLabel: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'space-between',
    color: theme.textColor,
  },
  menuItemDisplayName: {
    maxWidth: 300,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  selectTenantLink: {
    color: theme.nextButton,
    fontSize: theme.fontSizes.default,
    paddingLeft: 15,
  },
  iconLogout: {
    height: 'unset',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: 7,
  },
  logout: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.black,
  },
  iconCheck: {
    width: 25,
    marginLeft: 5,
    '& > span': {
      width: 25,
      minWidth: 25,
      height: 25,
    },
  },
  iconCancel: {
    marginLeft: 10,
    width: 25,
    '& > span': {
      background: `${theme.redStateColor} !important`,
      width: 25,
      minWidth: 25,
      height: 25,
      '&:hover': {
        background: theme.superlightGrey,
      },
    },
  },
  newTenant: {
    width: '100%',
  },
  myAccount: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  iconMyAccount: {
    width: 14,
    height: 14,
  },
  contextMenuItemMyAccount: {
    borderTop: theme.border,
    borderBottom: `${theme.border} !important`,
    padding: '20px 0',
    borderLeft: 'none',
    ...contextMenuItemNoHoverStyle,
  },
  email: {
    fontSize: theme.fontSizes.default,
    color: theme.textColor,
    maxWidth: 300,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // eslint plugin doesn't recognize animations used in other styles
  // eslint-disable-next-line
  '@keyframes borderSlideIn': {
    from: { transform: 'translate3d(100%, 0, 0)' },
    to: { transform: 'translate3d(0, 0, 0)' },
  },
  activeMobile: {
    '&:after': {
      content: '""',
      width: 0,
      height: 0,
      borderTop: '10px solid transparent',
      borderBottom: `10px solid ${theme.backgroundColor}`,
      borderRight: '10px solid transparent',
      borderLeft: '10px solid transparent',
      animationName: '$borderSlideIn',
      animationDuration: '0.5s',
      animationFillMode: 'forwards',
      position: 'absolute',
      right: 0,
      top: 19,
    },
  },
  isWSOffline: {
    position: 'absolute',
    right: 10,
    top: 30,
  },
  tooltip: {
    fontSize: '12px !important',
  },
  accountButtonWrapper: {
    cursor: 'pointer',
  },
}))

const AccountButton = (props) => {
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [tenantCreating, setTenantCreating] = useState(false)
  const [demoTenantCreating, setDemoTenantCreating] = useState(false)
  const [openCreateTenantModal, setOpenCreateTenantModal] = useState(false)
  const defaultUserInfo = useSelector(state => state.user.info)
  const isWSOffline = useSelector(state => state.global.isWSOffline)
  const [userInfo, setUserInfo] = useState(defaultUserInfo)
  const {
    activeTenant,
    tenants = [],
    hasTrial,
    hasDemo,
    isOwner,
    avatarImageUrl,
    hasConfirmedEmail,
    firstName,
    lastName,
  } = userInfo
  const { subscriptions, hasDanglingSubscriptions } = useSelector(state => state.subscription.details)
  const {
    tenantName: originalTenantName,
    tenantId,
    isDemo,
    companyName,
    email,
    backgroundColor,
    color,
  } = activeTenant
  const [tenantName, setTenantName] = useState(originalTenantName || companyName)
  const classes = useStyles({ hasConfirmedEmail, hasDanglingSubscriptions, tenantCreating, demoTenantCreating })
  const {
    toggleMenuOverlay,
    menuOverlayOpen,
    closeMenuOverlay,
    popoverOffset,
    useNewMenuComponents = false,
  } = props
  const isTaxAccountant = Boolean(subscriptions.find(sub => sub.isTaxAccountant))
  const allowAdditionalTenants = Boolean(subscriptions.find(sub => sub.allowAdditionalTenants))
  const { hasInvites } = useInvites()
  const accountButtonRef = useRef()
  const transformOrigin = popoverOffset || { horizontal: -65, vertical: 0 }

  useEffect(() => {
    setTenantName(originalTenantName || companyName)
  }, [originalTenantName, companyName])

  useEffect(() => {
    setUserInfo(defaultUserInfo)
  }, [defaultUserInfo])

  const dispatch = useDispatch()

  const openModal = (event) => {
    // unfortunately we cannot use redux at the moment as it redraws the complete app
    // and we need latest user info
    // and we cannot upate redux as this updates ampersand and redraws the complete app
    user.getUserInfo()
      .then(({ body: { data } }) => {
        setUserInfo(data)
        setShowModal(true)
      })
  }

  const closeModal = () => {
    setShowModal(false)
  }

  const selectTenant = (tenant, event) => {
    event && event.stopPropagation()
    closeModal()
    if (tenantId !== tenant.tenantId) {
      dispatch(switchTenant(tenant, selectTenantDone))
    }
  }

  const showEditDlg = () => {
    setShowEdit(true)
  }

  const cancelEditDlg = (elem) => {
    setShowEdit(false)
    setTenantName(originalTenantName || companyName)
  }

  const saveTenantName = ({ tenantName }) => {
    setShowEdit(false)
    dispatch(setTenantNameAction(tenantName))
  }

  const addNewTenant = (createDemoTenant = false) => {
    if (createDemoTenant) {
      setDemoTenantCreating(true)
    } else {
      setTenantCreating(true)
    }
    request(accountConfig.endpoints.postNewTenant, { auth: true, method: 'POST', data: { createDemoTenant } })
      .then(handleTenantCreation)
      .catch(handleTenantCreationSubmitFailure)
      .done(() => {
        if (isDemo) {
          setDemoTenantCreating(false)
        } else {
          setTenantCreating(false)
          navigate('/dashboard')
        }
      })
  }

  const handleTenantCreation = response => {
    dispatch(fetchData())
    toast.showToast({ type: 'info', message: lang.tenant.tenantCreated })
    const tenant = response.body.data?.tenant
    if (tenant) {
      dispatch(switchTenant({ tenantId: tenant }, selectTenantDone))
    }
  }

  const handleTenantCreationSubmitFailure = error => {
    const message = error.body?.message
    const tenantError = error.body?.meta?.tenant
    if (message) {
      if (tenantError) {
        if (tenantError[0].code === ErrorCodes.EXISTS) {
          toast.showToast({ type: 'error', message: lang.tenant.tenantTypeExists })
        } else {
          toast.showToast({ type: 'error', message })
        }
      } else {
        toast.showToast({ type: 'error', message })
      }
    } else {
      toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
    }
  }

  const logoutTenant = () => {
    dispatch(userLoggedOut())
    user.logout(true)
  }

  const selectTenantDone = (error, tenant) => {
    if (!error) {
      closeModal()
      toast.showToast({ type: 'info', message: lang.tenant.tenantActivated(getDisplayName(tenant)) })
    } else {
      if (error?.body?.meta?.switch) {
        toast.showToast({ type: 'error', message: lang.tenant.switchNotAllowed })
      } else {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
      }
    }
  }

  const getDisplayName = ({ tenantName, companyName }) => {
    return tenantName || companyName || (isTaxAccountant ? lang.tenant.newOffice : lang.tenant.newTenant)
  }

  const renderContextMenuItems = (tenant) => {
    return (
      <ContextMenuItem
        key={tenant.tenantId}
        icon={
          <TenantInitialsIcon
            iconColors={{ backgroundColor: tenant.backgroundColor, color: tenant.color }}
            tenantName={tenant.tenantName}
            companyName={tenant.companyName}
            isDemo={tenant.isDemo}
            customClass={{
              initialsIconDemo: clsx(classes.avatarDemo, !tenant.backgroundColor && classes.availableTenant, { menu: true }),
              initialsIcon: clsx(classes.avatar, !tenant.backgroundColor && classes.availableTenant, { menu: true }),
            }}
            icon={LogoColorIcon}
          />
        }
        label={(
          <span className={classes.menuItemLabel}>
            <span className={classes.menuItemDisplayName}>{getDisplayName(tenant)}</span>
            <a className={classes.selectTenantLink} onClick={(e) => selectTenant(tenant, e)}>{lang.tenant.switchLabel}</a>
          </span>
        )}
        labelCustomStyle={classes.menuItemLabel}
        iconCustomStyle={classes.iconStyle}
        className={classes.contextMenuItem}
        onItemClick={() => selectTenant(tenant)}>
      </ContextMenuItem>
    )
  }

  const contextMenuItems = tenants
    ? tenants.filter(tenant => tenantId !== tenant.tenantId && !tenant.isDemo).map(tenant => {
      return renderContextMenuItems(tenant)
    })
    : null

  const contextMenuDemoItems = tenants
    ? tenants.filter(tenant => tenantId !== tenant.tenantId && tenant.isDemo).map(tenant => {
      return renderContextMenuItems(tenant)
    })
    : null

  const tenantNameSection = () => {
    if (showEdit) {
      return (
        <div className={classes.inputContainer} >
          <Formik
            initialValues={{ tenantName }}
            onSubmit={saveTenantName}
            enableReinitialize={false} >
            <Form className={classes.inputForm}>
              <TextInput
                name='tenantName'
                className={classes.inputField}
                autoFocus={true}
                useFormik
              />
              <ButtonIconSquare
                icon={IconCheck}
                type='submit'
                className={classes.iconCheck}
                iconFill={theme.white}
              />
              <ButtonIconSquare
                icon={IconCancel}
                className={classes.iconCancel}
                onClick={cancelEditDlg}
                iconFill={theme.white}
              />
            </Form>
          </Formik>
          <span className={classes.email}>{email}</span>
        </div>
      )
    }
    return (
      <div className={classes.currentTenantName}>
        <span className={classes.displayName}>{getDisplayName({ tenantName, companyName })}</span>
        <span className={classes.email}>{email}</span>
      </div>
    )
  }

  const editIcon = !showEdit ? (<SVGIcon svg={IconPencil} {...ITEM_ICON_DIMENSIONS} className={classes.iconPencil} fill={theme.textColor} onClick={() => showEditDlg()} />) : null
  const createTenantButton = () => {
    if (isOwner && (!allowAdditionalTenants || hasTrial || isTaxAccountant)) return null
    else if (!isOwner && hasTrial) return null

    if (!tenantCreating) {
      return (
        <div className={classes.newTenant}>
          <ContextMenuItem
            label={(<span className={classes.menuItemLabel}>
              {lang.tenant.newTenant}
              <span className={classes.selectTenantLink}>{lang.tenant.addLabel}</span>
            </span>)}
            labelCustomStyle={classes.menuItemLabel}
            className={classes.contextMenuItem}
            iconCustomStyle={classes.iconStyle}
            icon={
              <TenantInitialsIcon
                tenantName={lang.tenant.newTenant}
                customClass={{
                  initialsIconDemo: clsx(classes.avatarDemo, classes.availableTenant, { menu: true }),
                  initialsIcon: clsx(classes.avatar, classes.availableTenant, { menu: true }),
                }}
              />
            }
            onItemClick={isOwner ? () => addNewTenant(false) : () => setOpenCreateTenantModal(true)}
          >
          </ContextMenuItem>
        </div>
      )
    }
    return (
      <div className={classes.newTenant}>
        <ContextMenuItem
          label={(<span className={classes.menuItemLabel}>
            {lang.tenant.creatingNewLabel}
          </span>)}
          labelCustomStyle={classes.menuItemLabel}
          className={classes.contextMenuItem}
          iconCustomStyle={classes.iconStyle}
          icon={
            <TenantInitialsIcon
              customClass={{
                initialsIconDemo: clsx(classes.avatarDemo, classes.availableTenant, { menu: true }),
                initialsIcon: clsx(classes.avatar, classes.availableTenant, { menu: true }),
              }}
            />
          }
        />
      </div>
    )
  }

  const createDemoTenantButton = () => {
    if (hasDemo === true || isOwner === false || isTaxAccountant) return null
    if (!demoTenantCreating) {
      return (
        <ContextMenuItem
          className={classes.contextMenuItem}
          iconCustomStyle={classes.iconStyle}
          labelCustomStyle={classes.menuItemLabel}
          label={(<span className={classes.menuItemLabel}>
            {lang.tenant.demoTenant}
            <span className={classes.selectTenantLink}>{lang.tenant.addLabel}</span>
          </span>)}
          icon={
            <TenantInitialsIcon
              isDemo
              customClass={{
                initialsIconDemo: clsx(classes.avatarDemo, classes.availableTenant, { menu: true }),
                initialsIcon: clsx(classes.avatar, classes.availableTenant, { menu: true }),
              }}
              icon={LogoColorIcon}
            />
          }
          onItemClick={() => addNewTenant(true)}
        >
        </ContextMenuItem>
      )
    }
    return (
      <div className={classes.newTenant}>
        <div className={classes.separator}></div>
        <ContextMenuItem
          label={(<span className={classes.menuItemLabel}>
            {lang.tenant.creatingNewDemoLabel}
          </span>)}
          labelCustomStyle={classes.menuItemLabel}
          className={classes.contextMenuItem}
          iconCustomStyle={classes.iconStyle}
          icon={
            <TenantInitialsIcon
              isDemo
              customClass={{
                initialsIconDemo: clsx(classes.avatarDemo, classes.availableTenant, { menu: true }),
                initialsIcon: clsx(classes.avatar, classes.availableTenant, { menu: true }),
              }}
              icon={LogoColorIcon}
            />
          }
        >
        </ContextMenuItem>
      </div>
    )
  }

  const tenantButton = createTenantButton()
  const demoButton = createDemoTenantButton()

  const contextMenu = (
    <>
      <Loader visible={tenantCreating || demoTenantCreating} text={lang.tenant.creatingTenant} className={classes.loader} />
      <div className={classes.menuWrapper}>
        <UserHeader hasInvites={hasInvites} isTaxConsultant={isTaxAccountant} />
        <ContextMenuItem
          onItemClick={() => {
            navigate('/my-account')
            closeMenuOverlay()
          }}
          labelCustomStyle={classes.menuItemLabel}
          label={<span className={classes.myAccount}>{lang.myAccount.title}<SVGIcon svg={ArrRightSvg} {...ITEM_ICON_DIMENSIONS} fill={theme.textColor} /></span>}
          iconCustomStyle={classes.iconMyAccount}
          className={classes.contextMenuItemMyAccount} />
        <div className={classes.currentTenant}>
          <div onClick={() => { navigate('/company') }}>
            <TenantInitialsIcon
              iconColors={{ backgroundColor, color }}
              tenantName={tenantName}
              companyName={companyName}
              isDemo={isDemo}
              customClass={{
                initialsIconDemo: clsx(classes.avatarDemo, { menu: true }),
                initialsIcon: clsx(classes.avatar, { menu: true }),
              }}
            />
          </div>
          <div className={classes.currentTenantDetail} >
            {tenantNameSection()}
          </div>
          {editIcon}
        </div>
        {contextMenuItems}
        <div className={classes.separator}></div>
        {contextMenuDemoItems}
        {tenantButton}
        {demoButton}
        <ContextMenuItem
          labelCustomStyle={classes.menuItemLabel}
          label={<span className={classes.logout}>{lang.logoutButtonLabel}<SVGIcon svg={IconLogout} {...ITEM_ICON_DIMENSIONS} fill={theme.black} /></span>}
          iconCustomStyle={classes.iconLogout}
          className={classes.contextMenuItemLogout}
          onItemClick={logoutTenant} />
      </div>
    </>)

  const contextMenuPopover = useNewMenuComponents
    ? (
      <AccountPopover
        open={showModal}
        hasInvites={hasInvites}
        isTaxAccountant={isTaxAccountant}
        onClose={closeModal}
        anchorEl={accountButtonRef?.current}
        transformOrigin={transformOrigin}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
        {contextMenu}
      </AccountPopover >
    )
    : (
      <Popover
        open={showModal}
        onClose={closeModal}
        anchorEl={accountButtonRef?.current}
        transformOrigin={transformOrigin}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
        {contextMenu}
      </Popover >
    )

  const mobileAccountButton = (
    <>
      <div className={clsx(classes.avatarPosition, menuOverlayOpen && classes.activeMobile)} onClick={toggleMenuOverlay}>
        <TenantInitialsIcon
          iconColors={{ backgroundColor: hasConfirmedEmail && !hasDanglingSubscriptions ? backgroundColor : theme.red, color }}
          hasConfirmedEmail={hasConfirmedEmail}
          tenantName={tenantName}
          hasDanglingSubscriptions={hasDanglingSubscriptions}
          companyName={companyName}
          isDemo={isDemo}
          customClass={{
            initialsIconDemo: classes.avatarDemo,
            initialsIcon: classes.avatar,
          }}
        />
      </div>
      <MenuOverlay
        open={menuOverlayOpen}
        closeMenuOverlay={closeMenuOverlay}>
        {menuOverlayOpen && contextMenu}
      </MenuOverlay>
    </>
  )

  const desktopAccountButton = (
    <>
      <div className={classes.accountButtonWrapper} ref={accountButtonRef} onClick={openModal} >
        {isWSOffline && <Tooltip classes={{ tooltip: classes.tooltip }} title={lang.wsOffline}>
          <SVGIcon svg={WarningIcon} className={classes.isWSOffline} fill={theme.colorHint} width="10px" height="10px" />
        </Tooltip>
        }
        {useNewMenuComponents
          ? (
            <TenantInitialsBubble
              animated
              displayErrors
              hasInvites={!isTaxAccountant && hasInvites}
              tenantName={[firstName, lastName].join(' ').trim() || tenantName}
              companyName={companyName}
              avatarUrl={avatarImageUrl}
              iconColors={{ backgroundColor, color }}
            />
          )
          : (
            <div className={classes.avatarButton}>
              {!avatarImageUrl
                ? <SVGIcon svg={IconUser} className={classes.avatarIcon} width="48px" height="48px" />
                : <img src={avatarImageUrl} />
              }
              <TenantInitialsIcon
                iconColors={{ backgroundColor: hasConfirmedEmail && !hasDanglingSubscriptions && !hasInvites ? backgroundColor : theme.red, color }}
                hasConfirmedEmail={hasConfirmedEmail}
                hasDanglingSubscriptions={hasDanglingSubscriptions}
                hasInvites={hasInvites}
                tenantName={tenantName}
                companyName={companyName}
                isDemo={isDemo}
                customClass={{
                  initialsIconDemo: classes.avatarDemo,
                  initialsIcon: classes.avatar,
                }} />
            </div>
          )
        }
      </div>
      {contextMenuPopover}
      {openCreateTenantModal && (
        <ConfirmModal
          open={true}
          debounceTimeConfirm={0}
          title={lang.myAccount.addTenantModal.title}
          description={lang.myAccount.addTenantModal.description}
          confirmButtonLabel={lang.myAccount.addTenantModal.confirmButton}
          onConfirm={() => addNewTenant(false)}
          onAbort={() => setOpenCreateTenantModal(false)} >
          <Loader visible={tenantCreating || demoTenantCreating} text={lang.tenant.creatingTenant} />
        </ConfirmModal>
      )
      }
    </>
  )

  return isMobileScreen() ? mobileAccountButton : desktopAccountButton
}

export default AccountButton
